'use es6';

import PropTypes from 'prop-types';
import { memo } from 'react';
import { isEmpty } from 'underscore';
import { useDrop } from 'react-dnd';
import { isFieldContactProperty } from 'FormsUI/utils/DragAndDrop';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DragTypes } from 'FormsUI/constants/DragAndDrop';
import { DropZone } from 'foundations-components/transitional/components/DropZone';
import { trackEditorInteraction } from 'FormsUI/utils/TrackingUtils';
import { DRAG_DROP_ADDED_FORM_FIELD_TO_QUEUED_PROGRESSIVE_FIELDS, DRAG_DROP_REORDERED_FORM_FIELD_TO_NEW_QUEUED_PROGRESSIVE_FIELD_GROUP } from 'FormsUI/constants/TrackingInteractionKeys/Editor/FormTab';
import { jsx as _jsx } from "react/jsx-runtime";
const DropZoneStates = {
  DEFAULT: 'default',
  HOVER: 'hover',
  ERROR: 'error'
};
const FormFieldDropTarget = /*#__PURE__*/memo(({
  formFieldGroupIndex,
  isQueued,
  addFormFieldGroup,
  moveFormFieldIntoNewFormFieldGroup
}) => {
  const [{
    hasDragOver,
    isDragSourceDroppable
  }, drop] = useDrop({
    accept: [DragTypes.NEW_FORM_FIELD, DragTypes.REORDER_FORM_FIELD],
    canDrop: item => {
      const draggedItem = item;
      const isRichTextEmpty = isEmpty(draggedItem.richTextContent);
      if (isQueued) {
        if (isFieldContactProperty(draggedItem)) {
          return isRichTextEmpty;
        }
        return false;
      }
      return isRichTextEmpty;
    },
    drop: (item, monitor) => {
      if (monitor.didDrop()) {
        return;
      }
      const draggedItem = item;
      const draggedItemType = monitor.getItemType();
      const targetFormFieldGroupIndex = formFieldGroupIndex;
      const isTargetQueued = isQueued;
      if (draggedItemType === DragTypes.NEW_FORM_FIELD) {
        const propertyId = draggedItem.propertyId;
        addFormFieldGroup(propertyId, targetFormFieldGroupIndex, {
          isQueued: isTargetQueued,
          newFieldType: draggedItem.fieldType
        });
        trackEditorInteraction(DRAG_DROP_ADDED_FORM_FIELD_TO_QUEUED_PROGRESSIVE_FIELDS);
      } else if (draggedItemType === DragTypes.REORDER_FORM_FIELD) {
        const sourceFormFieldGroupIndex = draggedItem.formFieldGroupIndex;
        const formFieldName = draggedItem.formFieldName;
        const formFieldPropertyObjectType = draggedItem.formFieldPropertyObjectType;
        moveFormFieldIntoNewFormFieldGroup(sourceFormFieldGroupIndex, formFieldName, formFieldPropertyObjectType, targetFormFieldGroupIndex, {
          isQueued: isTargetQueued
        });
        trackEditorInteraction(DRAG_DROP_REORDERED_FORM_FIELD_TO_NEW_QUEUED_PROGRESSIVE_FIELD_GROUP);
      }
    },
    collect: monitor => ({
      hasDragOver: monitor.isOver(),
      isDragSourceDroppable: monitor.canDrop()
    })
  });
  let dropZoneState = hasDragOver && isDragSourceDroppable ? DropZoneStates.HOVER : DropZoneStates.DEFAULT;
  if (hasDragOver && !isDragSourceDroppable) {
    dropZoneState = DropZoneStates.ERROR;
  }
  return /*#__PURE__*/_jsx("div", {
    ref: drop,
    children: /*#__PURE__*/_jsx(DropZone, {
      "data-test-id": "drop-zone",
      size: "small",
      use: dropZoneState,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'editor.form.formFields.dragandDropForm'
      })
    })
  });
});
FormFieldDropTarget.propTypes = {
  formFieldGroupIndex: PropTypes.number.isRequired,
  isQueued: PropTypes.bool,
  addFormFieldGroup: PropTypes.func.isRequired,
  moveFormFieldIntoNewFormFieldGroup: PropTypes.func.isRequired
};
export default FormFieldDropTarget;